import React, { useMemo, useEffect, useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  useBlockLayout,
  useExpanded,
  useGroupBy,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'

import statFilters from '../../constants/stat-filters'

dayjs.extend(utc)

const ReactTableStatistics = ({
  columns,
  component: Component,
  data: _data = [],
  defaultColumn,
  defaultOrder = 'desc',
  defaultSort,
  fetchData: _fetchData,
  hasSeasonColumn,
  isGrouped,
  isMobile,
  isPaginated,
  totalWidth,
  ...additionalProps
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [unsortedData, setUnsortedData] = useState(_data)
  const [pageCount, setPageCount] = useState(false)
  const fetchIdRef = useRef(0)
  const mounted = useRef(true)
  const currentFetchMethod = useRef(null)

  useEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false
    }
  }, [])

  const fetchData = useCallback(
    async (options = {}) => {
      if (typeof _fetchData !== 'function') {
        setIsLoading(false)
        return
      }
      setIsLoading(true)

      try {
        const { page, sort, sortBy } = options
        const { data, meta } = await _fetchData({ page, sort, sortBy })
        const fetchId = ++fetchIdRef.current

        if (mounted.current && fetchId === fetchIdRef.current) {
          setUnsortedData(data)
          if (meta && meta.pagination) setPageCount(meta.pagination.total_pages)
          setIsLoading(false)
        }
      } catch (e) {
        setIsLoading(false)
      }
    },
    [_fetchData]
  )

  const data = useMemo(() => {
    if (!Array.isArray(unsortedData)) {
      return unsortedData
    }

    const filteredData = unsortedData.filter((d) => d && d.team !== null)

    if (isGrouped) {
      // SN-844: The grouping done by react-table seems to reverse the order of the
      // data, so reverse it back
      return filteredData.reverse()
    }

    return filteredData
  }, [isGrouped, unsortedData])

  const expandedState = useMemo(() => {
    if (!isGrouped) return {}

    return data.reduce((collector, datum) => {
      if (datum.schedule && datum.schedule.id)
        collector[
          `division:${datum.schedule.id}/-/-/${datum.schedule.name}`
        ] = true
      return collector
    }, {})
  }, [data, isGrouped])

  const initialSortBy = useMemo(
    () => [
      {
        id: statFilters.abbreviations[defaultSort],
        desc: defaultOrder === 'desc',
      },
    ],
    [defaultOrder, defaultSort]
  )

  const initialState = {
    expanded: expandedState,
    groupBy: ['division'],
    sortBy: initialSortBy,
  }
  let tableSettings = {
    columns,
    data,
    defaultColumn,
    initialState,
    disableMultiSort: true,
  }

  if (isPaginated) {
    initialState.pageIndex = 0
    tableSettings = {
      ...tableSettings,
      manualSortBy: true,
      manualPagination: true,
      autoResetPage: false,
      pageCount,
    }
  }

  const table = useTable(
    tableSettings,
    useBlockLayout,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination
  )

  const {
    page,
    rows,
    state: { pageIndex, sortBy },
  } = table

  useEffect(() => {
    const task = requestAnimationFrame(() => !isPaginated && fetchData())
    return () => cancelAnimationFrame(task)
  }, [fetchData, isPaginated])

  // Add in pagination/sort parameters to the request method
  useEffect(() => {
    let task
    if (typeof fetchData === 'function' && isPaginated) {
      const baseArgs = { page: pageIndex + 1 }

      if (Array.isArray(sortBy) && sortBy[0]) {
        let sortId = sortBy[0].id

        switch (sortId) {
          case 'name':
            sortId = 'player.name'
            break

          case 'Team':
            sortId = 'team.name'
            break

          case 'PTS':
            sortId = 'GP'
            break
        }

        baseArgs.sortBy = sortId
        baseArgs.sort = sortBy[0].desc ? 'desc' : 'asc'
      }

      const methodToRun = fetchData.bind(this, baseArgs)
      currentFetchMethod.current = methodToRun
      task = requestAnimationFrame(() => methodToRun())
    }
    return () => task && cancelAnimationFrame(task)
  }, [isPaginated, fetchData, pageIndex, sortBy])

  return (
    <Component
      columns={columns}
      hasSeasonColumn={hasSeasonColumn}
      initialState={initialState}
      isLoading={isLoading}
      isMobile={isMobile}
      isPaginated={isPaginated}
      rows={isPaginated ? page : rows}
      fetchData={currentFetchMethod?.current || fetchData}
      table={table}
      totalWidth={totalWidth}
      {...additionalProps}
    />
  )
}

ReactTableStatistics.propTypes = {
  columns: PropTypes.array.isRequired,
  component: PropTypes.func.isRequired,
  defaultColumn: PropTypes.object.isRequired,
  defaultOrder: PropTypes.string,
  defaultSort: PropTypes.string,
  fetchData: PropTypes.func.isRequired,
  hasSeasonColumn: PropTypes.bool,
  isGrouped: PropTypes.bool,
  isMobile: PropTypes.bool,
  isPaginated: PropTypes.bool,
  totalWidth: PropTypes.number,
}

export default ReactTableStatistics
